import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { PageWithPermissions, PageAction, PageId, ActionCode } from 'types/index';
import { settingsService } from 'services/settings.service';
import { tokenStorage } from 'utils/storage';
import { getAppPathname, isRegistrationPage } from 'utils/publicApi';
import { ROUTES } from 'constants/index';
import { refreshDashboardQueries } from 'hooks/queries/useDashboardQueries';

interface PermissionsContextType {
  pages: PageWithPermissions[];
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  hasPermission: (pageId: PageId, actionCode: ActionCode) => boolean;
  getPagePermissions: (pageId: PageId) => PageAction[];
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const location = useLocation();
  const [pages, setPages] = useState<PageWithPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  const invalidateAuthenticatedQueries = useCallback(() => {
    refreshDashboardQueries();
  }, []);

  const loadPermissions = useCallback(async () => {
    const isBackgroundRefresh = pagesRef.current.length > 0;
    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const pagesWithPermissions = await settingsService.getPagesWithPermissions();
      setPages(pagesWithPermissions);
    } catch (err: any) {
      setError(err?.message || 'Failed to load permissions');
      console.error('Error loading permissions:', err);
    } finally {
      setHasLoaded(true);
      if (!isBackgroundRefresh) {
        setIsLoading(false);
      }
    }
  }, []);

  const shouldLoadPermissionsForPath = useCallback((pathname: string) => {
    const path = pathname.toLowerCase();
    const onLogin =
      path === ROUTES.LOGIN || path.endsWith(ROUTES.LOGIN);
    return !onLogin;
  }, []);

  const syncPermissionsWithSession = useCallback(
    async (pathname: string) => {
      const token = tokenStorage.getAccessToken();
      if (!token?.trim()) {
        setPages([]);
        setHasLoaded(false);
        return;
      }
      if (!shouldLoadPermissionsForPath(pathname)) {
        return;
      }
      await loadPermissions();
      invalidateAuthenticatedQueries();
    },
    [
      loadPermissions,
      invalidateAuthenticatedQueries,
      shouldLoadPermissionsForPath,
    ]
  );

  useEffect(() => {
    void syncPermissionsWithSession(getAppPathname());

    const handler = () => {
      void syncPermissionsWithSession(getAppPathname());
    };

    window.addEventListener('auth:token-changed', handler as EventListener);
    return () => {
      window.removeEventListener('auth:token-changed', handler as EventListener);
    };
  }, [syncPermissionsWithSession]);

  useEffect(() => {
    void syncPermissionsWithSession(getAppPathname());
  }, [location.pathname, syncPermissionsWithSession]);

  const lastFocusRefreshRef = useRef(0);
  const PERMISSIONS_FOCUS_DEBOUNCE_MS = 30_000;

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (!tokenStorage.hasValidToken() || isRegistrationPage()) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < PERMISSIONS_FOCUS_DEBOUNCE_MS) {
        return;
      }
      lastFocusRefreshRef.current = now;
      void loadPermissions();
    };

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [loadPermissions]);

  const hasPermission = (pageId: PageId, actionCode: ActionCode): boolean => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return false;
    
    const action = page.actions.find(a => a.actionCode === actionCode);
    return action?.hasPermission || false;
  };

  const getPagePermissions = (pageId: PageId): PageAction[] => {
    const page = pages.find(p => p.id === pageId);
    return page?.actions || [];
  };

  const refreshPermissions = async () => {
    await loadPermissions();
    invalidateAuthenticatedQueries();
  };

  const value: PermissionsContextType = {
    pages,
    isLoading,
    hasLoaded,
    error,
    hasPermission,
    getPagePermissions,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const usePermission = (pageId: PageId, actionCode: ActionCode): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(pageId, actionCode);
};
